import * as THREE from '@anov/3d-core'
import { addUpdateTag } from '../decorators'
import vertexShader from './shader/vertex'
import fragmentShader from './shader/fragment'
import type { GridMaterialInterface } from '.'

type GridMaterialParameters = {
  side?: THREE.Side
  transparent?: boolean
  theme?: THREE.Color
  majorLineWidth?: number
  minorLineWidth?: number
  axisLineWidth?: number
  majorGridDiv?: number
  gridDiv?: number
  majorLineColor?: THREE.Color
  minorLineColor?: THREE.Color
  xAxisColor?: THREE.Color
  zAxisColor?: THREE.Color
}

/**
 * 高性能网格材质
 */
export class GridMaterial extends THREE.ShaderMaterial implements GridMaterialInterface {
  private _majorLineWidth = 0.04
  private _minorLineWidth = 0.01
  private _axisLineWidth = 0.15
  private _majorGridDiv = 10.0
  private _gridDiv = 4.0
  private _majorLineColor = new THREE.Color()
  private _minorLineColor = new THREE.Color()
  private _xAxisColor = new THREE.Color(1, 0.3, 0.3)
  private _zAxisColor = new THREE.Color(0.3, 0.3, 1)
  private _baseColor = new THREE.Color('#707070')

  public get majorLineWidth() {
    return this.majorLineWidth
  }

  @addUpdateTag
  public set majorLineWidth(value: number) {
    this._majorLineWidth = value
  }

  public get minorLineWidth() {
    return this._minorLineWidth
  }

  @addUpdateTag
  public set minorLineWidth(value: number) {
    this._minorLineWidth = value
  }

  public get axisLineWidth() {
    return this._axisLineWidth
  }

  @addUpdateTag
  public set axisLineWidth(value: number) {
    this._axisLineWidth = value
  }

  public get majorGridDiv() {
    return this._majorGridDiv
  }

  @addUpdateTag
  public set majorGridDiv(value: number) {
    this._majorGridDiv = value
  }

  public get gridDiv() {
    return this._gridDiv
  }

  @addUpdateTag
  public set gridDiv(value: number) {
    this._gridDiv = value
  }

  public get majorLineColor() {
    return this._majorLineColor
  }

  @addUpdateTag
  public set majorLineColor(value: THREE.Color) {
    this._majorLineColor = value
  }

  public get minorLineColor() {
    return this._minorLineColor
  }

  @addUpdateTag
  public set minorLineColor(value: THREE.Color) {
    this._minorLineColor = value
  }

  public get xAxisColor() {
    return this._xAxisColor
  }

  @addUpdateTag
  public set xAxisColor(value: THREE.Color) {
    this._xAxisColor = value
  }

  public get zAxisColor() {
    return this._zAxisColor
  }

  @addUpdateTag
  public set zAxisColor(value: THREE.Color) {
    this._zAxisColor = value
  }

  public get baseColor() {
    return this._baseColor
  }

  @addUpdateTag
  public set baseColor(value: THREE.Color) {
    this._baseColor = value
  }

  constructor(options?: GridMaterialParameters) {
    super()

    const { side, transparent, theme, majorLineWidth, minorLineWidth, axisLineWidth, majorGridDiv, gridDiv, majorLineColor, minorLineColor, xAxisColor, zAxisColor } = options || {}

    this._majorLineWidth = majorLineWidth
    this._minorLineWidth = minorLineWidth
    this._axisLineWidth = axisLineWidth
    this._majorGridDiv = majorGridDiv
    this._gridDiv = gridDiv
    this._majorLineColor = majorLineColor
    this._minorLineColor = minorLineColor
    this._xAxisColor = xAxisColor
    this._zAxisColor = zAxisColor
    this._baseColor = theme

    this.side = side ?? THREE.DoubleSide
    this.transparent = transparent ?? true
    this.vertexShader = vertexShader
    this.fragmentShader = fragmentShader
    this.uniforms = {
      ...this.uniforms,
      u_baseAlpha: { value: 0.5 },
      u_majorLineWidth: { value: this._majorLineWidth },
      u_minorLineWidth: { value: this._minorLineWidth },
      u_axisLineWidth: { value: this._axisLineWidth },
      u_majorGridDiv: { value: this._majorGridDiv },
      u_gridDiv: { value: this._gridDiv },
      u_majorLineColor: { value: this._majorLineColor },
      u_minorLineColor: { value: this._minorLineColor },
      u_baseColor: { value: this._baseColor },
      u_xAxisColor: { value: this._xAxisColor },
      u_zAxisColor: { value: this._zAxisColor },
    }
  }
}
